import { match } from 'ts-pattern';

import {
  AccountDetails,
  ACHAccountDetails,
  COPAccountDetails,
  CVUAccountDetails,
  PIXAccountDetails,
  SpeiAccountDetails,
  SwiftAccountDetails,
  WireAccountDetails,
} from '@/types/contacts';
import {
  isACHAccountDetails,
  isCOPAccountDetails,
  isCVUAccountDetails,
  isPIXAccountDetails,
  isSpeiAccountDetails,
  isSwiftAccountDetails,
  isWireAccountDetails,
} from '@/types/contacts.ext';
import { CountryType } from '@/types/country';
import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';
import { getCurrencyName } from '@/utility/formatAmount';

export const getAccountCurrency = (accountDetails: AccountDetails): CurrencyCodeType =>
  <CurrencyCodeType>match(accountDetails)
    .when(isWireAccountDetails, (): CurrencyCodeType => CurrencyCodeType.USD)
    .when(isACHAccountDetails, (): CurrencyCodeType => CurrencyCodeType.USD)
    .when(isCOPAccountDetails, (): CurrencyCodeType => CurrencyCodeType.COP)
    .when(isPIXAccountDetails, (): CurrencyCodeType => CurrencyCodeType.BRL)
    .when(isSpeiAccountDetails, (): CurrencyCodeType => CurrencyCodeType.MXN)
    .when(isCVUAccountDetails, (): CurrencyCodeType => CurrencyCodeType.ARS)
    .when(isSwiftAccountDetails, (): CurrencyCodeType => CurrencyCodeType.USD)
    .exhaustive();

export const getAccountCurrencyName = (locale: Locale, accountDetails: AccountDetails): string => {
  if (isSwiftAccountDetails(accountDetails)) {
    const currencyName = getCurrencyName(locale, getAccountCurrency(accountDetails));

    return `${currencyName}`;
  } else {
    return getCurrencyName(locale, getAccountCurrency(accountDetails));
  }
};

export const getAccountDescription = (
  banksByCountry: Partial<Record<CountryType, Record<string, string>>>,
  accountDetails: AccountDetails,
): string => {
  return match(accountDetails)
    .when(isWireAccountDetails, (details: WireAccountDetails): string => {
      const {
        wireDetails: { bankInfo },
      } = details;
      return `${bankInfo.name} - ${bankInfo.bic}`;
    })
    .when(isACHAccountDetails, (details: ACHAccountDetails): string => {
      const {
        achDetails: { bankInfo, personInfo },
      } = details;
      return `${bankInfo.name} - ${personInfo.accountNumber}`;
    })
    .when(isCOPAccountDetails, (details: COPAccountDetails): string => {
      const {
        copDetails: { bankCode, bankAccount },
      } = details;
      const bankName = banksByCountry[CountryType.CO]?.[bankCode] ?? 'Unknown bank';
      return `${bankName} - ${bankAccount}`;
    })
    .when(isPIXAccountDetails, (): string => 'PIX')
    .when(isSpeiAccountDetails, (): string => 'SPEI')
    .when(isCVUAccountDetails, (details: CVUAccountDetails): string => {
      const {
        cvuDetails: { cvu },
      } = details;
      return `CVU - ${cvu}`;
    })
    .when(isSwiftAccountDetails, (details: SwiftAccountDetails): string => {
      const {
        swiftDetails: { bankInfo },
      } = details;

      return `${bankInfo.name} - ${bankInfo.swiftCode}`;
    })
    .exhaustive();
};

export const getBankName = (
  accountDetails: AccountDetails,
  banksByCountry: Partial<Record<CountryType, Record<string, string>>>,
): string | null => {
  return match(accountDetails)
    .when(isPIXAccountDetails, (): null => null)
    .when(isSpeiAccountDetails, (): null => null)
    .when(isCVUAccountDetails, (): null => null)
    .when(isCOPAccountDetails, (details: COPAccountDetails): string => {
      const {
        copDetails: { bankCode, bankAccount },
      } = details;
      const bankName = banksByCountry[CountryType.CO]?.[bankCode] ?? 'Unknown bank';
      return `${bankName} - ${bankAccount}`;
    })
    .when(isWireAccountDetails, (details: WireAccountDetails): string => {
      const {
        wireDetails: { bankInfo },
      } = details;
      return bankInfo.name;
    })
    .when(isACHAccountDetails, (details: ACHAccountDetails): string => {
      const {
        achDetails: { bankInfo },
      } = details;
      return bankInfo.name;
    })
    .when(isSwiftAccountDetails, (details: SwiftAccountDetails): string => {
      const {
        swiftDetails: { bankInfo },
      } = details;
      return bankInfo.name;
    })
    .exhaustive();
};

export const getAccountNumber = (accountDetails: AccountDetails): string | null => {
  return match(accountDetails)
    .when(isWireAccountDetails, (details: WireAccountDetails): string => {
      const {
        wireDetails: { personInfo },
      } = details;
      return personInfo.accountNumber;
    })
    .when(isACHAccountDetails, (details: ACHAccountDetails): string => {
      const {
        achDetails: { personInfo },
      } = details;
      return personInfo.accountNumber;
    })
    .when(isCOPAccountDetails, (details: COPAccountDetails): string => {
      const {
        copDetails: { bankAccount },
      } = details;
      return bankAccount;
    })
    .when(isPIXAccountDetails, (details: PIXAccountDetails): string => {
      const {
        pixDetails: { pixKey },
      } = details;
      return pixKey;
    })
    .when(isSpeiAccountDetails, (details: SpeiAccountDetails): string => {
      const {
        speiDetails: { clabe },
      } = details;
      return clabe;
    })
    .when(isCVUAccountDetails, (details: CVUAccountDetails): string => {
      const {
        cvuDetails: { cvu },
      } = details;
      return cvu;
    })
    .when(isSwiftAccountDetails, (details: SwiftAccountDetails): string | null => {
      const {
        swiftDetails: { personInfo, businessInfo },
      } = details;
      return personInfo?.iban ?? businessInfo?.accountNumber ?? null;
    })
    .exhaustive();
};
